import { Box, Image, Text, VStack } from "@chakra-ui/react";
import { FC, useCallback, useEffect, useRef, useState } from "react";

export type ContentElementPosition = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type OverlayMetricMode = "clicks" | "goals" | "both";

export type ContentPreviewElement = {
  name?: string;
  clickRate: number;
  goalRate: number;
  position: ContentElementPosition | null | undefined;
};

export type Props = {
  previewImageUrl: string | null | undefined;
  viewportWidth: number | null | undefined;
  viewportHeight: number | null | undefined;
  elements: ContentPreviewElement[];
  overlayMode?: OverlayMetricMode;
  imageWidth?: string;
};

const CLOSE_BUTTON_NAME = "閉じるボタン";
const MIN_FONT_SIZE = 14;

const OVERLAY_STYLES: Record<
  OverlayMetricMode,
  { bg: string; border: string }
> = {
  clicks: {
    bg: "rgba(230, 126, 34, 0.55)",
    border: "1px solid rgba(211, 84, 0, 0.85)",
  },
  goals: {
    bg: "rgba(0, 0, 255, 0.5)",
    border: "1px solid rgba(0, 0, 255, 0.8)",
  },
  both: {
    bg: "rgba(47, 104, 186, 0.55)",
    border: "1px solid rgba(47, 104, 186, 0.9)",
  },
};

const formatRate = (rate: number): string => `${parseFloat(rate.toFixed(2))}%`;

const OverlayLabel: FC<{
  fontSize: number;
  children: string;
}> = ({ fontSize, children }) => (
  <Text
    fontSize={`${fontSize}px`}
    fontWeight="bold"
    color="white"
    lineHeight="1.05"
    textAlign="center"
    whiteSpace="nowrap"
    sx={{
      WebkitTextStroke: "1px black",
      paintOrder: "stroke fill",
    }}
  >
    {children}
  </Text>
);

const resolveFontSize = (
  boxW: number,
  boxH: number,
  overlayMode: OverlayMetricMode
): number => {
  const minDim = Math.min(boxW, boxH);
  const maxDim = Math.max(boxW, boxH);
  const widthBased = maxDim / (overlayMode === "both" ? 5.5 : 4.5);
  const heightBased =
    minDim * (overlayMode === "both" ? 0.38 : boxH <= 50 ? 0.55 : 0.4);
  const fitted = Math.min(widthBased, heightBased);

  if (overlayMode === "both") {
    return Math.max(MIN_FONT_SIZE, Math.min(18, fitted));
  }
  return Math.max(MIN_FONT_SIZE, Math.min(20, fitted));
};

const resolveDisplayBox = (
  boxW: number,
  boxH: number,
  left: number,
  top: number,
  name: string | undefined,
  overlayMode: OverlayMetricMode
): { left: number; top: number; width: number; height: number } => {
  const isCloseButton = name === CLOSE_BUTTON_NAME;
  if (!isCloseButton) {
    return { left, top, width: boxW, height: boxH };
  }

  // Widen close-button overlays so 14px rate labels fit comfortably.
  const minW = overlayMode === "both" ? 78 : 64;
  const minH = overlayMode === "both" ? 42 : 28;
  const width = Math.max(boxW, minW);
  const height = Math.max(boxH, minH);

  return {
    left: left + boxW / 2 - width / 2,
    top: top + boxH / 2 - height / 2,
    width,
    height,
  };
};

export const ContentPreviewImage: FC<Props> = ({
  previewImageUrl,
  viewportWidth,
  elements,
  overlayMode = "clicks",
  imageWidth = "100%",
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [scale, setScale] = useState<number | null>(null);

  const updateScale = useCallback(() => {
    if (imgRef.current && viewportWidth) {
      setScale(imgRef.current.clientWidth / viewportWidth);
    }
  }, [viewportWidth]);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      updateScale();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScale]);

  if (!previewImageUrl) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        minHeight="200px"
        bg="#F5F5F5"
        color="#999999"
        fontSize="13px"
      >
        プレビュー画像がありません
      </Box>
    );
  }

  const style = OVERLAY_STYLES[overlayMode];

  return (
    <Box
      width="100%"
      maxHeight="80vh"
      overflowY="auto"
      display="flex"
      justifyContent="center"
      border="1px solid #DADADA"
      borderRadius="4px"
    >
      <Box position="relative" width={imageWidth} overflow="visible">
        <Image
          ref={imgRef}
          src={previewImageUrl}
          width="100%"
          display="block"
          onLoad={updateScale}
        />
        {scale !== null &&
          elements.map((element, i) => {
            if (!element.position) return null;
            const { x, y, width, height } = element.position;
            const boxW = width * scale;
            const boxH = height * scale;
            const display = resolveDisplayBox(
              boxW + 2,
              boxH + 2,
              x * scale - 1,
              y * scale - 1,
              element.name,
              overlayMode
            );
            const fontSize = resolveFontSize(
              display.width,
              display.height,
              overlayMode
            );

            return (
              <Box
                key={i}
                position="absolute"
                left={`${display.left}px`}
                top={`${display.top}px`}
                width={`${display.width}px`}
                height={`${display.height}px`}
                bg={style.bg}
                border={style.border}
                pointerEvents="none"
                overflow="visible"
                zIndex={element.name === CLOSE_BUTTON_NAME ? 2 : 1}
              >
                <Box
                  position="absolute"
                  left="50%"
                  top="50%"
                  transform="translate(-50%, -50%)"
                  whiteSpace="nowrap"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {overlayMode === "clicks" && (
                    <OverlayLabel fontSize={fontSize}>
                      {formatRate(element.clickRate)}
                    </OverlayLabel>
                  )}
                  {overlayMode === "goals" && (
                    <OverlayLabel fontSize={fontSize}>
                      {formatRate(element.goalRate)}
                    </OverlayLabel>
                  )}
                  {overlayMode === "both" && (
                    <VStack spacing="0" alignItems="center">
                      <OverlayLabel fontSize={fontSize}>
                        {formatRate(element.clickRate)}
                      </OverlayLabel>
                      <OverlayLabel fontSize={fontSize}>
                        {formatRate(element.goalRate)}
                      </OverlayLabel>
                    </VStack>
                  )}
                </Box>
              </Box>
            );
          })}
      </Box>
    </Box>
  );
};
